import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { toast } from 'react-toastify';

import styles from './QrCodes.module.sass';
import { exportUnusedQrCodes, uploadQrCodes } from '@core';
import { IQrCodeStats, IQrCodeUploadResult } from '@types';
import { getApiBlobErrorMessage, getApiErrorMessage } from '../errors';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.xlsx', '.csv'];

interface QrCodesProps {
  eventId: string;
  stats: IQrCodeStats | undefined;
  onUploaded: (stats: IQrCodeStats) => void;
}

const QrCodes: React.FC<QrCodesProps> = ({ eventId, stats, onUploaded }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<IQrCodeUploadResult>();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportUnusedQrCodes(eventId);
    } catch (err) {
      toast.error(await getApiBlobErrorMessage(err, 'ექსპორტისას დაფიქსირდა შეცდომა'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }

    const name = file.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some(ext => name.endsWith(ext))) {
      toast.error('ფაილის ფორმატი არასწორია. დასაშვებია მხოლოდ .xlsx და .csv ფაილები');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('ფაილის ზომა არ უნდა აღემატებოდეს 5MB-ს');
      return;
    }

    setIsUploading(true);
    setResult(undefined);
    try {
      const response = await uploadQrCodes(eventId, file);
      setResult(response.data);
      onUploaded(response.data.stats);
      toast.success(`დაემატა ${response.data.added} ახალი QR კოდი`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'ფაილის დამუშავებისას დაფიქსირდა შეცდომა'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.head}>
        <div className={styles.title}>QR კოდები</div>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          className={styles.input}
          onChange={e => void handleFileChange(e)}
        />
        <button
          className={cn('button', 'button-small', 'button-stroke')}
          disabled={isExporting || !stats || stats.available === 0}
          onClick={() => void handleExport()}
        >
          {isExporting ? 'მზადდება...' : 'გამოუყენებლების ექსპორტი'}
        </button>
        <button
          className={cn('button', 'button-small', 'button-blue')}
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? 'იტვირთება...' : 'ფაილის ატვირთვა'}
        </button>
      </div>

      <div className={styles.stats}>
        <div className={cn(styles.stat, styles.total)}>
          <span>სულ ატვირთული</span>
          <strong>{stats?.total ?? '-'}</strong>
        </div>
        <div className={cn(styles.stat, styles.assigned)}>
          <span>გაცემული</span>
          <strong>{stats?.assigned ?? '-'}</strong>
        </div>
        <div className={cn(styles.stat, styles.available, { [styles.empty]: stats && stats.available === 0 })}>
          <span>ხელმისაწვდომი</span>
          <strong>{stats?.available ?? '-'}</strong>
        </div>
      </div>

      <div className={styles.hint}>
        .xlsx ან .csv ფაილი, ბმულები პირველ სვეტში. სათაური (header) არასავალდებულოა, განმეორებული ბმულები გამოტოვდება.
      </div>

      {result && (
        <div className={styles.result}>
          <div>დაემატა: {result.added}</div>
          {result.alreadyUploaded > 0 && <div>უკვე ატვირთული იყო (გამოტოვდა): {result.alreadyUploaded}</div>}
          {result.duplicatesInFile > 0 && <div>ფაილში განმეორებული (გამოტოვდა): {result.duplicatesInFile}</div>}
          {result.invalidCount > 0 && (
            <div className={styles.error}>
              არასწორი ბმული {result.invalidCount} რიგში: {result.invalidRows.join(', ')}
              {result.invalidCount > result.invalidRows.length ? '...' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QrCodes;
